/*
 * Brand variants for the demo email. Selected at runtime via ?brand= in the URL.
 *
 * The default (no param, or unknown value) is `generic`: a neutral "Home Services"
 * email with no retailer logo and the page-level Veritact palette. `?brand=walmart`
 * opts into the Walmart-skinned variant. New brands are added by extending the
 * BrandId union and adding an entry here — see CLAUDE.md § Brand variants.
 */
export type BrandId = 'generic' | 'walmart' | 'costco'

export interface Brand {
  /** Stable id; also the value of ?brand= in the URL. */
  id: BrandId
  /** Display name shown next to the avatar in the email header. */
  senderName: string
  /** Path to the retailer logo under web/public/. Null = no logo anywhere. */
  logoSrc: string | null
  /** CSS class scoping the brand palette; empty string for the generic variant. */
  emailClassName: string
}

export const BRANDS: Record<BrandId, Brand> = {
  generic: {
    id: 'generic',
    senderName: 'Home Services',
    logoSrc: null,
    emailClassName: '',
  },
  walmart: {
    id: 'walmart',
    senderName: 'Walmart Home Services',
    logoSrc: '/demo/walmart-logo.png',
    emailClassName: 'walmart-email',
  },
  costco: {
    id: 'costco',
    senderName: 'Costco Home Services',
    logoSrc: '/demo/costco-logo.png',
    emailClassName: 'costco-email',
  },
}

const isKnownBrand = (raw: string | null): raw is BrandId =>
  raw !== null && raw in BRANDS

/**
 * Read the active brand from the current URL.
 *
 * Cheap and stateless — safe to call inside a render. Returns `BRANDS.generic`
 * for missing, empty, unknown, or case-mismatched values.
 */
export const activeBrand = (): Brand => {
  const raw = new URLSearchParams(window.location.search).get('brand')
  return isKnownBrand(raw) ? BRANDS[raw] : BRANDS.generic
}
